import { useCallback, useEffect, useState } from 'react';
import { retryAfterOf } from '../api/problem';
import type { NotificationChannel, NotificationPreferenceResponse } from '../api/types';

/**
 * Pulls the token out of whatever an invitee pastes: the whole link from the text message, the link with
 * its surrounding text, or the bare token.
 *
 * Tokens are 32 random bytes in base64url, 43 characters, so anything shorter than 20 is taken for a typo
 * rather than sent to the API to be refused.
 */
export function extractInvitationToken(input: string): string | null {
  const text = input.trim();
  if (!text) return null;

  const fromLink = /[?&]token=([^&\s#]+)/.exec(text)?.[1];
  const candidate = fromLink ? safeDecode(fromLink) : text;

  return /^[A-Za-z0-9_-]{20,}$/.test(candidate) ? candidate : null;
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    // A malformed escape is not a token either; leaving it undecoded lets the shape check refuse it.
    return value;
  }
}

/** How long to wait before a code can be asked for again. Codes cost the tenant money and arrive slowly. */
export const CODE_RESEND_SECONDS = 60;

/**
 * A countdown in whole seconds, for "send again in 45 seconds".
 *
 * Driven by one interval that stops itself at zero, so a screen left open does not keep a timer running. Given the
 * last send's error, a refusal for coming too often counts down the wait the API named (`Retry-After`, NR-2), from
 * what is left of it: that can be a quarter of an hour, and a button enabled sooner is only refused again.
 */
export function useCountdown(refusal: unknown = null): { remaining: number; start: (seconds?: number) => void } {
  const [remaining, setRemaining] = useState(0);
  const running = remaining > 0;

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => setRemaining((value) => Math.max(0, value - 1)), 1000);
    return () => clearInterval(timer);
  }, [running]);

  const start = useCallback((seconds: number = CODE_RESEND_SECONDS) => setRemaining(seconds), []);

  useEffect(() => {
    const wait = retryAfterOf(refusal);
    if (wait) start(wait);
  }, [refusal, start]);

  return { remaining, start };
}

/** The draft of the preferences grid: `typeId:channel` to the value the person has chosen. */
export type PreferenceDraft = Record<string, boolean>;

export function draftKey(typeId: number, channel: NotificationChannel): string {
  return `${typeId}:${channel}`;
}

/**
 * What one checkbox in the preferences grid shows, and whether it can be changed.
 *
 * Locked channels (in-app for the safety alerts) are always on. Email and SMS are greyed out while in-app
 * is off, because every other copy is made from the in-app one and would not arrive either
 * (decision 2026-09-24 #6).
 */
export function channelControl(
  preference: NotificationPreferenceResponse,
  channel: NotificationChannel,
  draft: PreferenceDraft,
): { offered: boolean; checked: boolean; disabled: boolean } {
  const entry = preference.channels.find((c) => c.channel === channel);
  if (!entry) return { offered: false, checked: false, disabled: true };

  const value = (c: NotificationChannel) => {
    const found = preference.channels.find((x) => x.channel === c);
    if (!found) return false;
    if (found.locked) return true;
    return draft[draftKey(preference.notificationTypeId, c)] ?? found.enabled;
  };

  const inAppOn = value('inApp');
  return {
    offered: true,
    checked: value(channel),
    disabled: entry.locked || (channel !== 'inApp' && !inAppOn),
  };
}
